import _ from 'lodash';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { KEY_RECOIL, TYPE_BEAT_ENUM } from '@/shared/config';
import { storeTimeout } from '@/shared/lib/storeTimeout';
import { checkAndProcessBeatData, generateUpdatedBeatsV2 } from '@/entities/beat';
import { checkAndProcessEcgData } from '@/entities/ecg';
import { logError } from '@/shared/lib/apiHelpers';
import {
  findChangeBeat,
  findChangeReviewed,
  findChangeSwap,
  hasTemplateBeat,
} from '@/widgets/beats/ui/reviewBeatsHelper';
import {
  selectBeatChanges,
  selectBulkBeatsChanges,
  selectBulkSwapBeats,
  selectResetBulkBeatsChanges,
  selectResetSwapBeats,
  selectSwapBeatChanges,
  useReportStore,
} from '@/entities/report';
import type { BeatPreviewStripProps } from './beatPreviewStrip';

/**
 * Backs one small beat preview strip in the RR-interval beat grid, loading its ECG snippet and reporting how the beat has been edited. Fetches samples and beats around the beat, replays beat-type, reviewed and swap changes with their bulk and reset counterparts, prefetches the ten-second window, derives whether the beat differs from its original and which morphology template name to show, and clears pending timers when the active tab changes.
 *
 * @param props - The preview inputs carrying the beat, its index, the ECG data map, offset, channels, active tab and the select callback.
 * @returns The strip render state: ECG samples, beat markers, HES statuses, channels, the type and reviewed changes, a changed flag, the template label and the click handler.
 */
const useBeatPreviewStrip = ({
  activeButton,
  ecgDataMap,
  beat,
  offset,
  index,
  channels: channelsProp,
  onClickSelectBeat: onClickSelectBeatProp,
}: Pick<
  BeatPreviewStripProps,
  'activeButton' | 'ecgDataMap' | 'beat' | 'offset' | 'index' | 'channels' | 'onClickSelectBeat'
>) => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;
  const [ecgData, setEcgData] = useState<any[]>([]);
  const [beatMarkers, setBeatMarkers] = useState<any[]>([]);
  const [hesBeatStatus, setHesBeatStatus] = useState<any[]>([]);
  const timeoutRef = useRef<any>();

  const beatChanges = useReportStore(selectBeatChanges(keyRecoil));
  const bulkBeatsChanges = useReportStore(selectBulkBeatsChanges(keyRecoil));
  const resetBulkBeatsChanges = useReportStore(selectResetBulkBeatsChanges(keyRecoil));

  const reviewedBeatsChanges = useReportStore((s) => s.reviewedBeatsChanges);
  const bulkReviewedBeats = useReportStore((s) => s.bulkReviewedBeats);
  const resetBulkReviewedBeats = useReportStore((s) => s.resetBulkReviewedBeats);

  const swapBeatChanges = useReportStore(selectSwapBeatChanges(keyRecoil));
  const bulkSwapBeats = useReportStore(selectBulkSwapBeats(keyRecoil));
  const resetSwapBeats = useReportStore(selectResetSwapBeats(keyRecoil));

  const originalTemplateOverview = useReportStore((s) => s.originalTemplateOverview);

  let isActiveQuery: boolean;
  // Cache data for ecgViewer
  /**
   * Warms the ten-second ECG cache for a preview beat so opening the full viewer is instant. Schedules a delayed fetch of samples around the beat, deregisters the timer from the shared timeout store when it runs, logs any failure, and registers the timer so leaving the active tab can cancel it.
   *
   * @param waitTime - Milliseconds to wait before starting the prefetch.
   * @returns Nothing.
   */
  const download10SCacheECG = (waitTime = 0) => {
    const timerId = setTimeout(() => {
      storeTimeout.removeStore(activeButton, timerId);
      try {
        checkAndProcessEcgData({
          ecgDataMap,
          beat,
          offset: 5000,
          dataLength: ecgDataMap.samplingFrequency * 10,
        });
      } catch (error) {
        logError(error.message, 'Error');
      }
    }, waitTime);
    timeoutRef.current = timerId;
    storeTimeout.pushStore(activeButton, timerId);
  };

  /**
   * Loads the short ECG snippet and beats behind one preview strip in the beat grid. Fetches samples and beat data around the beat, discards them if the strip moved on meanwhile, replays stored beat changes with their bulk and reset lists onto the markers, kicks off the ten-second cache prefetch staggered by the strip index, nulls the buffers for garbage collection, and swallows cancelled requests.
   *
   * @returns A promise resolving once the snippet, markers and HES statuses are in local state.
   */
  const downloadBeat = async () => {
    if (beat?.id) {
      try {
        const dataLength = ecgDataMap.samplingFrequency * 2;

        // Check and process ECG data
        let ecgData = await checkAndProcessEcgData({
          ecgDataMap,
          beat,
          offset,
          dataLength,
        });

        // Check and process beat data
        let beatData = await checkAndProcessBeatData({
          beat,
          offset,
          ecgDataMap,
        });
        if (isActiveQuery) {
          // checkAndProcessEcgData's inferred return type (common/handlerEcgData.ts, Task 7) resolves to
          // unknown via processEcgData - pre-existing gap.
          setEcgData(ecgData as any);
          if (beatData) {
            const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeatsV2({
              beats: beatData?.beats,
              hesBeatStatus: beatData?.hesBeatStatus,
              beatChanges,
              startEpoch: moment(beat.id - offset).valueOf(),
              stopEpoch: moment(beat.id + offset).valueOf(),
              beatHeatMapIds: beatData?.beatHeatMapIds,
              beatTemplateIds: beatData?.beatTemplateIds,
              applyBulkBeatsChanges: bulkBeatsChanges,
              resetAppliedBulkBeatsChanges: resetBulkBeatsChanges,
            });
            setBeatMarkers(updatedBeats);
            setHesBeatStatus(updatedHesBeatStatus);
          }
          download10SCacheECG(beat.type === TYPE_BEAT_ENUM.Q ? 0 : index * 100);
        }

        // Nullify the data objects to help garbage collection
        ecgData = null;
        beatData = null;
      } catch (error) {
        if (!_.includes(error.message, 'canceled')) {
          logError(error.message, 'Error');
        }
      }
    }
  };

  /**
   * Selects the previewed beat when its strip is clicked without a modifier key. Bails out on shift, control or meta clicks so multi-select gestures are left alone, and otherwise reports the beat and its index upward.
   *
   * @param e - The click event whose modifier keys are inspected.
   * @returns Nothing.
   */
  const onClickSelectBeat = (e: any) => {
    if (e?.shiftKey || e?.ctrlKey || e?.metaKey) {
      return;
    }
    onClickSelectBeatProp(beat, index);
  };

  useEffect(() => {
    isActiveQuery = true;
    downloadBeat();
    return () => {
      isActiveQuery = false;
    };
  }, [beat?.id, beatChanges, bulkBeatsChanges, resetBulkBeatsChanges]);

  useEffect(
    () => () => {
      storeTimeout.removeStore(activeButton, timeoutRef.current);
    },
    [activeButton],
  );
  const channels = channelsProp?.includes(4) ? [beat.channel] : channelsProp;

  const beatsTypeChange = useMemo(
    () =>
      findChangeBeat({
        changes: beatChanges,
        bulkChanges: bulkBeatsChanges,
        resetBulkChange: resetBulkBeatsChanges,
        beat,
      }),
    [beatChanges, bulkBeatsChanges, resetBulkBeatsChanges, beat],
  );

  const beatReviewedChange = useMemo(
    () =>
      findChangeReviewed({
        changes: reviewedBeatsChanges,
        bulkChanges: bulkReviewedBeats,
        resetBulkChange: resetBulkReviewedBeats,
        beat,
      }),
    [reviewedBeatsChanges, bulkReviewedBeats, resetBulkReviewedBeats, beat],
  );

  const beatSwapChange = useMemo(
    () =>
      findChangeSwap({
        changes: swapBeatChanges,
        bulkChanges: bulkSwapBeats,
        resetBulkChange: resetSwapBeats,
        beat,
      }),
    [swapBeatChanges, bulkSwapBeats, resetSwapBeats, beat],
  );

  const isChanged = useMemo(() => {
    const isChangedType = beatsTypeChange?.originalType
      ? beatsTypeChange?.type !== beatsTypeChange?.originalType
      : beatsTypeChange?.type !== beatsTypeChange?.oldType;
    const isChangedReviewed =
      beatReviewedChange?.originalReviewed !== beatReviewedChange?.isReviewed;
    const isChangedSwap =
      beatSwapChange?.templateId &&
      beatSwapChange?.templateId !== beatSwapChange?.originalTemplateId;
    return isChangedType || isChangedReviewed || isChangedSwap;
  }, [beatsTypeChange, beatReviewedChange, beatSwapChange]);

  const displayTemplateId = useMemo(() => {
    if (!hasTemplateBeat(activeButton)) return '';
    const morphology = originalTemplateOverview[activeButton] || [];
    const findTemplate = _.find(
      morphology,
      (template) => template.id === beatSwapChange?.templateId,
    );
    return findTemplate?.name || '#1';
  }, [originalTemplateOverview, beatSwapChange.templateId, activeButton]);

  return {
    ecgData,
    beatMarkers,
    hesBeatStatus,
    channels,
    beatsTypeChange,
    beatReviewedChange,
    isChanged,
    displayTemplateId,
    onClickSelectBeat,
  };
};

export default useBeatPreviewStrip;
